import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  createHomeSlider2,
  getHomeSlider2,
  updateHomeSlider2,
} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object({
  logo1: Yup.mixed()
    .test("required", "Logo 1 is required", function (value) {
      const { old_logo1 } = this.parent;
      return !!(value || old_logo1);
    })
    .test("fileType", "Only image files are allowed", (value) => {
      if (!value || typeof value === 'string') return true;
      return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
    }),
  logo2: Yup.mixed()
    .test("required", "Logo 2 is required", function (value) {
      const { old_logo2 } = this.parent;
      return !!(value || old_logo2);
    })
    .test("fileType", "Only image files are allowed", (value) => {
      if (!value || typeof value === 'string') return true;
      return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
    }),
  logo3: Yup.mixed()
    .test("required", "Logo 3 is required", function (value) {
      const { old_logo3 } = this.parent;
      return !!(value || old_logo3);
    })
    .test("fileType", "Only image files are allowed", (value) => {
      if (!value || typeof value === 'string') return true;
      return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
    }),
});

export default function SlidertwoEdit() {
  const { id, partnerId } = useParams();

  const [slidertwo, setSlidertwo] = useState({
    logo1: "",
    logo2: "",
    logo3: "",
    index: null,
  });
  const inputRefs = [useRef(null), useRef(null), useRef(null)];
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initial form values
  const getInitialValues = () => ({
    logo1: slidertwo.logo1 || "",
    logo2: slidertwo.logo2 || "",
    logo3: slidertwo.logo3 || "",
    old_logo1: slidertwo.logo1 || "",
    old_logo2: slidertwo.logo2 || "",
    old_logo3: slidertwo.logo3 || "",
  });

 const fetchSliderTwo = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }
    try {
      const response = await getHomeSlider2(id);
      const slidertwoData = response.data.data[0];
      setSlidertwo({
        ...slidertwoData,
        index: response.data.data.indexOf(slidertwoData),
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: error.response.data.message || 'Failed to fetch slider 2. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchSliderTwo();
  }, [id]);

  const handleSubmit = async (values, { setFieldValue }) => {
    setSaving(true);

    var formData = new FormData();

    if (values.logo1 instanceof File) {
      formData.append("logo1", values.logo1);
    }

    if (values.logo2 instanceof File) {
      formData.append("logo2", values.logo2);
    }

    if (values.logo3 instanceof File) {
      formData.append("logo3", values.logo3);
    }

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const res = await createHomeSlider2(formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || "Slider 2 section has been created successfully!",
          });
          const slidertwoData = res.data.data;
          setSlidertwo(slidertwoData);
          if (slidertwoData && slidertwoData.id) {
            navigate(`/${partnerId}/home/slider2/edit/${slidertwoData.id}`);
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || "Failed to create slider 2 section. Please try again.",
          });
        }
      } catch (err) {
        showToast({
          type: 'error',
          message: err?.response?.data?.message || "Failed to create slider 2 section. Please try again.",
        });
        console.log(err);
      }
    } else {
      try {
        const res = await updateHomeSlider2(id, formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || "Slider 2 section has been updated successfully!",
          });
          
          // Update the form fields with the returned logo URLs if available
          const updatedSliderData = res?.data?.data;
          if (updatedSliderData) {
            if (updatedSliderData.logo1) {
              setFieldValue("logo1", updatedSliderData.logo1);
              setFieldValue("old_logo1", updatedSliderData.logo1);
            }
            if (updatedSliderData.logo2) {
              setFieldValue("logo2", updatedSliderData.logo2);
              setFieldValue("old_logo2", updatedSliderData.logo2);
            }
            if (updatedSliderData.logo3) {
              setFieldValue("logo3", updatedSliderData.logo3);
              setFieldValue("old_logo3", updatedSliderData.logo3);
            }
            setSlidertwo(prev => ({
              ...prev,
              ...updatedSliderData,
            }));
          }
          
          // Clear the file inputs
          inputRefs.forEach(ref => {
            if (ref.current) ref.current.value = '';
          });
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || "Failed to update slider 2 section. Please try again.",
          });
        }
      } catch (err) {
        showToast({
          type: 'error',
          message: err?.response?.data?.message || "Failed to update slider 2 section. Please try again.",
        });
        console.log(err);
      }
    }
    
    setSaving(false);
  };

  return (
    <>
    {isLoading ? <CmsCircularProgress/> : 
      <FormWrapper>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            getFieldProps,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Title>{Number(id) ? 'Edit' : 'Add' }  Slider 2 section</Title>
              <br />
              <SliderDiv>
                <SubTitle>Logo 1</SubTitle>

                <FormGroup
                  label="Select a Logo"
                  requiredField
                  error={touched.logo1 && errors.logo1 ? errors.logo1 : ''}
                >
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFieldValue("logo1", file);
                    }}
                    onBlur={getFieldProps('logo1').onBlur}
                    style={{ display: "none" }}
                    ref={inputRefs[0]}
                    name="logo1"
                    isInvalid={touched.logo1 && !!errors.logo1}
                  />
                  {values.logo1  ? (
                    <ImageWrapper>
                      <UploadImage>
                        <BootstrapImage
                          src={values.logo1 instanceof File ? URL.createObjectURL(values.logo1) : values.logo1}
                          alt="logo1"
                          onClick={() => {
                            inputRefs[0].current.click();
                          }}
                        />
                      </UploadImage>
                      <br />
                      {(values.logo1 instanceof File || (values.logo1 && values.logo1 !== values.old_logo1)) && (
                      <RemoveButtonWrapper>
                        <BootstrapButton
                          type="button"
                          onClick={() => {
                            setFieldValue("logo1", values.old_logo1 || null);
                            if (inputRefs[0].current) {
                              inputRefs[0].current.value = '';
                            }
                          }}
                        >
                          <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                        </BootstrapButton>
                      </RemoveButtonWrapper>
                      )}
                    </ImageWrapper>
                  ) : (Number(id) && (values.logo1 || values.old_logo1)) ? (
                    <UploadImage>
                      <BootstrapImage
                        src={values.logo1 || values.old_logo1}
                        alt="logo1"
                        onClick={() => {
                          inputRefs[0].current.click();
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = 0.8;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = 1;
                        }}
                      />
                    </UploadImage>
                  ) : (
                    <div>
                      <Button
                        className="w-auto mb-0"
                        onClick={() => {
                          inputRefs[0].current.click();
                        }}
                      >
                        Upload the image
                      </Button>
                    </div>
                  )}
                                    
                </FormGroup>
                <SubTitle className="mt-3">Logo 2</SubTitle>
                <FormGroup
                  label="Select a Logo"
                  requiredField
                  error={touched.logo2 && errors.logo2 ? errors.logo2 : ''}
                >
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFieldValue("logo2", file);
                    }}
                    onBlur={getFieldProps('logo2').onBlur}
                    style={{ display: "none" }}
                    ref={inputRefs[1]}
                    name="logo2"
                    isInvalid={touched.logo2 && !!errors.logo2}
                  />
                  {values.logo2  ? (
                    <ImageWrapper>
                      <UploadImage>
                        <BootstrapImage
                          src={values.logo2 instanceof File ? URL.createObjectURL(values.logo2) : values.logo2}
                          alt="logo2"
                          onClick={() => {
                            inputRefs[1].current.click();
                          }}
                        />
                      </UploadImage>
                      {(values.logo2 instanceof File || (values.logo2 && values.logo2 !== values.old_logo2)) && (
                      <RemoveButtonWrapper>
                        <BootstrapButton
                          type="button"
                          onClick={() => {
                            setFieldValue("logo2", values.old_logo2 || null);
                            if (inputRefs[1].current) {
                              inputRefs[1].current.value = '';
                            }
                          }}
                        >
                          <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                        </BootstrapButton>
                      </RemoveButtonWrapper>
                      )}
                    </ImageWrapper>
                  ) : (Number(id) && (values.logo2 || values.old_logo2)) ? (
                    <UploadImage>
                      <BootstrapImage
                        src={values.logo2 || values.old_logo2}
                        alt="logo2"
                        onClick={() => {
                          inputRefs[1].current.click();
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = 0.8;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = 1;
                        }}
                      />
                    </UploadImage>
                  ) : (
                    <div>
                      <Button
                        className="w-auto mb-0"
                        onClick={() => {
                          inputRefs[1].current.click();
                        }}
                      >
                        Upload the image
                      </Button>
                    </div>
                  )}
                                  
                </FormGroup>
                <SubTitle className="mt-3">Logo 3</SubTitle>
                <FormGroup
                  label='Select a Logo'
                  requiredField
                  error={touched.logo3 && errors.logo3 ? errors.logo3 : ''}
                >
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFieldValue("logo3", file);
                    }}
                    onBlur={getFieldProps('logo3').onBlur}
                    style={{ display: "none" }}
                    ref={inputRefs[2]}
                    name="logo3"
                    isInvalid={touched.logo3 && !!errors.logo3}
                  />
                  {values.logo3  ? (
                    <ImageWrapper>
                      <UploadImage>
                        <BootstrapImage
                          src={values.logo3 instanceof File ? URL.createObjectURL(values.logo3) : values.logo3}
                          alt="logo3"
                          onClick={() => {
                            inputRefs[2].current.click();
                          }}
                        />
                      </UploadImage>
                      <br />
                      {(values.logo3 instanceof File || (values.logo3 && values.logo3 !== values.old_logo3)) && (
                      <RemoveButtonWrapper>
                        <BootstrapButton
                          type="button"
                          onClick={() => {
                            setFieldValue("logo3", values.old_logo3 || null);
                            if (inputRefs[2].current) {
                              inputRefs[2].current.value = '';
                            }
                          }}
                        >
                          <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                        </BootstrapButton>
                      </RemoveButtonWrapper>
                      )}
                    </ImageWrapper>
                  ) : (Number(id) && (values.logo3 || values.old_logo3)) ? (
                    <UploadImage>
                      <BootstrapImage
                        src={values.logo3 || values.old_logo3}
                        alt="logo3"
                        onClick={() => {
                          inputRefs[2].current.click();
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = 0.8;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = 1;
                        }}
                      />
                    </UploadImage>
                  ) : (
                    <div>
                      <Button
                        onClick={() => {
                          inputRefs[2].current.click();
                        }}
                        className="w-auto mb-0"
                      >
                        Upload the image
                      </Button>
                    </div>
                  )}
                           
                </FormGroup>
              
                <ButtonWrapper className="mt-3">
                  <Link
                    to={`/${partnerId}/home/slider2/view/${Number(id) ? id : "create"}`}
                  >
                    <Button variant="secondary" className="w-100">Go Back</Button>
                  </Link>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving || isSubmitting}
                    className="w-100"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </ButtonWrapper>
                </SliderDiv>
              </Form>
            )}
          </Formik>
        </FormWrapper>}
    </>
    
  );
}

const FormWrapper = styled.div`
  @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h5``;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  // margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
       @media (max-width:576px) {
          top: -9px;
        right: -17px;
  }
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
   @media (max-width: 890px) {
   justify-content: end;
    gap: 2rem;
  }
   @media (max-width: 425px) {
  gap: 1rem;
}
`;

const UploadImage = styled.div`
  max-width: 15vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 40vw;
  }
`;
const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));
const ImageWrapper = styled.div`
    position:relative;
    max-width: 15vw;
   @media (max-width:576px) {
   max-width: 40vw;
  }
`;

const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));
